import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { useTranslation } from "../i18n/LanguageProvider";
import { toast } from "sonner";
import { format } from "date-fns";
import { de, enUS, sk } from "date-fns/locale";
import {
  Timer,
  FolderOpen,
  CalendarCheck,
  Clock,
  Play,
  Square,
  ChevronRight,
  MapPin,
  LogOut,
  Briefcase,
  CheckCircle2,
  User,
  ArrowLeft,
  Navigation,
  Navigation2,
  Phone,
  FileText,
  ImageIcon,
  Building2,
  HardHat,
  Loader2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ImpersonationBanner from "../components/ImpersonationBanner";

const API = "/api";

// ──────────── Bottom Nav ────────────
const TAB_KEYS = [
  { id: "clock", icon: Timer, labelKey: "ws.stempeluhr" },
  { id: "projects", icon: FolderOpen, labelKey: "ws.projekte" },
  { id: "today", icon: CalendarCheck, labelKey: "ws.heute" },
  { id: "profile", icon: User, labelKey: "ws.profil" },
];

function BottomNav({ active, onChange, t }) {
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="workspace-bottom-nav"
    >
      <div className="flex">
        {TAB_KEYS.map((tab) => {
          const Icon = tab.icon;
          const isActive = active === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onChange(tab.id)}
              className={`flex-1 flex flex-col items-center pt-2 pb-1 transition-colors ${
                isActive ? "text-gray-900" : "text-gray-400"
              }`}
              data-testid={`nav-tab-${tab.id}`}
            >
              <Icon className="w-6 h-6" strokeWidth={isActive ? 2.2 : 1.5} />
              <span className="text-[10px] font-medium mt-0.5">{t(tab.labelKey)}</span>
              {isActive && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] bg-gray-900 rounded-b" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ──────────── GPS Helper ────────────
function getGPSPosition(timeout = 10000) {
  return new Promise((resolve, reject) => {
    console.log("[GPS] Requesting position via navigator.geolocation...");
    if (!navigator.geolocation) {
      console.error("[GPS] navigator.geolocation NOT available");
      reject(new Error("Geolocation not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        console.log("[GPS] Position received:", pos.coords.latitude, pos.coords.longitude, "accuracy:", pos.coords.accuracy);
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      },
      (err) => {
        console.error("[GPS] Position error:", err.code, err.message);
        reject(err);
      },
      { enableHighAccuracy: true, timeout, maximumAge: 30000 }
    );
  });
}

// ──────────── GPS Consent Dialog (Mobile) ────────────
function MobileGPSConsentDialog({ open, onAccept, onDecline }) {
  const [checked, setChecked] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" data-testid="mobile-gps-consent-overlay">
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-6 pb-8 animate-in slide-in-from-bottom" data-testid="mobile-gps-consent-dialog">
        <div className="flex items-center gap-2 mb-3">
          <Navigation className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">Standorterfassung</h3>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Ihr Standort wird <strong>nur beim Ein- und Ausstempeln</strong> erfasst — keine dauerhafte Überwachung.
        </p>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4 space-y-2">
          <p className="flex items-start gap-2 text-xs text-blue-800"><MapPin className="w-4 h-4 mt-0.5 shrink-0" />Nachweis des Arbeitsorts für die Zeiterfassung</p>
          <p className="flex items-start gap-2 text-xs text-blue-800"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />Erfassung nur beim Stempeln, nicht dauerhaft</p>
        </div>
        <label className="flex items-start gap-2 mb-5 cursor-pointer">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 rounded border-gray-300 text-blue-600 w-4 h-4" data-testid="mobile-gps-consent-checkbox" />
          <span className="text-xs text-gray-700 leading-tight">Ich habe verstanden, dass GPS-Daten bei der Zeiterfassung erfasst werden.</span>
        </label>
        <div className="flex gap-3">
          <button onClick={onDecline} className="flex-1 h-12 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 active:bg-gray-100" data-testid="mobile-gps-decline-btn">Abbrechen</button>
          <button onClick={onAccept} disabled={!checked} className="flex-1 h-12 bg-blue-600 disabled:bg-gray-300 rounded-xl text-sm font-semibold text-white active:bg-blue-700" data-testid="mobile-gps-accept-btn">Standort erlauben</button>
        </div>
      </div>
    </div>
  );
}

// ──────────── TAB: Stempeluhr ────────────
function ClockTab({ user, projects, clockEntry, onRefresh, t, dateLoc }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedProject, setSelectedProject] = useState("");
  const [loading, setLoading] = useState(false);

  // GPS state
  const [gpsConsent, setGpsConsent] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsPermission, setGpsPermission] = useState("unknown");
  const [showGpsConsent, setShowGpsConsent] = useState(false);
  const [lastGpsCoords, setLastGpsCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const pendingActionRef = useRef(null);
  const gpsEnabled = user?.permissions?.gps_tracking_enabled === true;

  console.log("[ClockTab] Render — gpsEnabled:", gpsEnabled, "gpsPermission:", gpsPermission, "gpsConsent:", gpsConsent);

  const isClockedIn = clockEntry && clockEntry.clock_in && !clockEntry.clock_out;
  const activeProjects = projects.filter(
    (p) => p.is_active !== false && p.status === "aktiv"
  );

  // Set default project
  useEffect(() => {
    if (isClockedIn && clockEntry?.project_id) {
      setSelectedProject(clockEntry.project_id);
    } else if (!selectedProject && activeProjects.length === 1) {
      setSelectedProject(activeProjects[0].id);
    }
  }, [activeProjects, isClockedIn, clockEntry, selectedProject]);

  // Tick
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // GPS permission check (works in Chrome, fallback for Safari)
  useEffect(() => {
    if (!gpsEnabled) { console.log("[GPS] gpsEnabled=false, skipping permission check"); return; }
    // Check for server-side consent
    if (user?.gps_consent_timestamp) {
      console.log("[GPS] Server-side consent found:", user.gps_consent_timestamp);
      setGpsConsent(true);
    }
    // Browser Permissions API (Chrome/Firefox — NOT supported on Safari iOS)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        console.log("[GPS] Permissions API result:", result.state);
        setGpsPermission(result.state);
        if (result.state === "granted") setGpsConsent(true);
        // If browser DENIED, override server-side consent
        if (result.state === "denied") {
          console.log("[GPS] Browser denied — resetting gpsConsent");
          setGpsConsent(false);
        }
        result.onchange = () => {
          console.log("[GPS] Permission changed to:", result.state);
          setGpsPermission(result.state);
          if (result.state === "granted") setGpsConsent(true);
          if (result.state === "denied") setGpsConsent(false);
        };
      }).catch((e) => {
        console.log("[GPS] Permissions API not available:", e.message);
        setGpsPermission("prompt"); // Assume prompt for Safari
      });
    } else {
      console.log("[GPS] navigator.permissions not available (Safari?), setting prompt");
      setGpsPermission("prompt");
    }
  }, [gpsEnabled, user?.gps_consent_timestamp]);

  const elapsed = useCallback(() => {
    if (!isClockedIn) return null;
    const start = new Date(clockEntry.clock_in);
    const diff = Math.max(0, Math.floor((currentTime - start) / 1000));
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return `${h.toString().padStart(2, "0")}:${m
      .toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }, [isClockedIn, clockEntry, currentTime]);

  // GPS Consent accepted → request actual GPS position
  const handleGpsAccepted = async () => {
    console.log("[GPS] User accepted consent dialog");
    setShowGpsConsent(false);
    setGpsConsent(true);
    setGpsError(null);

    // Save server-side consent
    try { await authFetch(`${API}/users/me/consent/gps`, { method: "PUT" }); } catch {}

    // Now actually request GPS
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action === "clock-in") {
      await executeClockInWithGPS();
    } else if (action === "clock-out") {
      await executeClockOutWithGPS();
    }
  };

  const handleGpsDeclined = () => {
    console.log("[GPS] User declined consent");
    setShowGpsConsent(false);
    pendingActionRef.current = null;
    setLoading(false);
    toast.error("GPS-Zustimmung erforderlich zum Einstempeln");
  };

  const executeClockInWithGPS = async () => {
    let pid = selectedProject;
    if (!pid && activeProjects.length === 1) pid = activeProjects[0].id;
    console.log("[GPS] executeClockInWithGPS for project:", pid);
    setLoading(true);
    setGpsLoading(true);
    setGpsError(null);

    try {
      const location = await getGPSPosition();
      console.log("[GPS] Got position for clock-in:", location);
      setGpsPermission("granted");
      setGpsConsent(true);
      setLastGpsCoords(location);
      setGpsLoading(false);

      const res = await authFetch(`${API}/clock/in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: pid, break_minutes: 0, location }),
      });
      if (res.ok) {
        toast.success(t("ws.eingestempelt_toast", "Eingestempelt!"));
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common.fehler"));
      }
    } catch (err) {
      console.error("[GPS] Clock-in GPS failed:", err);
      setGpsPermission("denied");
      setGpsLoading(false);
      setGpsError(err.message || "GPS fehlgeschlagen");
      toast.error("GPS-Zugriff verweigert — Einstempeln nicht möglich");
    } finally {
      setLoading(false);
    }
  };

  const executeClockOutWithGPS = async () => {
    console.log("[GPS] executeClockOutWithGPS");
    setLoading(true);
    setGpsLoading(true);
    setGpsError(null);

    try {
      const location = await getGPSPosition();
      console.log("[GPS] Got position for clock-out:", location);
      setGpsPermission("granted");
      setGpsConsent(true);
      setLastGpsCoords(location);
      setGpsLoading(false);

      const res = await authFetch(`${API}/clock/out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location }),
      });
      if (res.ok) {
        toast.success(t("ws.ausgestempelt_toast", "Ausgestempelt!"));
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common.fehler"));
      }
    } catch (err) {
      console.error("[GPS] Clock-out GPS failed:", err);
      setGpsPermission("denied");
      setGpsLoading(false);
      setGpsError(err.message || "GPS fehlgeschlagen");
      toast.error("GPS-Zugriff verweigert — Ausstempeln nicht möglich");
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    let pid = selectedProject;
    if (!pid && activeProjects.length === 1) pid = activeProjects[0].id;
    if (!pid) {
      toast.error(t("ws.bitte_projekt"));
      return;
    }
    console.log("[GPS] handleClockIn — gpsEnabled:", gpsEnabled, "gpsConsent:", gpsConsent);

    if (gpsEnabled) {
      if (!gpsConsent) {
        // Show consent dialog first
        console.log("[GPS] Showing consent dialog before clock-in");
        pendingActionRef.current = "clock-in";
        setShowGpsConsent(true);
        return;
      }
      // Already consented — request GPS directly
      await executeClockInWithGPS();
      return;
    }

    // GPS not enabled — clock in without location
    setLoading(true);
    try {
      const res = await authFetch(`${API}/clock/in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: pid, break_minutes: 0 }),
      });
      if (res.ok) {
        toast.success(t("ws.eingestempelt_toast", "Eingestempelt!"));
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common.fehler"));
      }
    } catch {
      toast.error(t("common.fehler"));
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    console.log("[GPS] handleClockOut — gpsEnabled:", gpsEnabled, "gpsConsent:", gpsConsent);

    if (gpsEnabled) {
      if (!gpsConsent) {
        console.log("[GPS] Showing consent dialog before clock-out");
        pendingActionRef.current = "clock-out";
        setShowGpsConsent(true);
        return;
      }
      await executeClockOutWithGPS();
      return;
    }

    // GPS not enabled
    setLoading(true);
    try {
      const res = await authFetch(`${API}/clock/out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        toast.success(t("ws.ausgestempelt_toast", "Ausgestempelt!"));
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common.fehler"));
      }
    } catch {
      toast.error(t("common.fehler"));
    } finally {
      setLoading(false);
    }
  };

  const projectName =
    projects.find((p) => p.id === (clockEntry?.project_id || selectedProject))
      ?.name || "";

  return (
    <div className="flex flex-col min-h-full px-5 pt-6 pb-4" data-testid="clock-tab">
      {/* GPS Consent Dialog */}
      <MobileGPSConsentDialog open={showGpsConsent} onAccept={handleGpsAccepted} onDecline={handleGpsDeclined} />

      {/* GPS Status Banner */}
      {gpsEnabled && (
        <div className="mb-4 space-y-1.5">
          {gpsPermission === "denied" ? (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl" data-testid="mobile-gps-denied-banner">
              <Navigation className="w-3.5 h-3.5 text-red-600 shrink-0" />
              <span className="text-xs text-red-700 font-semibold">GPS blockiert — Bitte in Browsereinstellungen aktivieren</span>
            </div>
          ) : gpsPermission === "granted" || gpsConsent ? (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-xl" data-testid="mobile-gps-active-banner">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-green-700 font-semibold flex-1">GPS aktiv</span>
              {gpsLoading && <Loader2 className="w-3.5 h-3.5 text-green-600 animate-spin" />}
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl" data-testid="mobile-gps-required-banner">
              <Navigation className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span className="text-xs text-amber-700 font-semibold">GPS Tracking erforderlich — Zustimmung beim Stempeln</span>
            </div>
          )}
          {/* GPS Debug Info */}
          {lastGpsCoords && (
            <div className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg" data-testid="mobile-gps-coords">
              <p className="text-[10px] text-gray-500 font-mono">
                GPS: {lastGpsCoords.lat.toFixed(5)}, {lastGpsCoords.lng.toFixed(5)} (±{Math.round(lastGpsCoords.accuracy)}m)
              </p>
            </div>
          )}
          {gpsError && (
            <div className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg" data-testid="mobile-gps-error">
              <p className="text-[10px] text-red-600 font-medium">GPS Fehler: {gpsError}</p>
            </div>
          )}
        </div>
      )}

      {/* Date + Time */}
      <div className="text-center mb-6">
        <p className="text-gray-500 text-sm">
          {format(currentTime, "EEEE, dd. MMMM yyyy", { locale: dateLoc })}
        </p>
        <p
          className="text-5xl font-bold text-gray-900 font-mono mt-1 tracking-tight"
          data-testid="clock-time"
        >
          {format(currentTime, "HH:mm")}
        </p>
        <p className="text-gray-400 text-lg font-mono">
          {format(currentTime, "ss")}
        </p>
      </div>

      {/* Status */}
      <div className="text-center mb-6">
        {isClockedIn ? (
          <>
            <span className="inline-flex items-center gap-2 text-green-700 font-semibold text-base">
              <span className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
              {t("ws.eingestempelt", "Eingestempelt")}
            </span>
            <p className="text-sm text-gray-500 mt-1">
              {t("ws.seit", "seit")} {format(new Date(clockEntry.clock_in), "HH:mm")}
              {projectName && <> &middot; {projectName}</>}
            </p>
            <p
              className="text-3xl font-bold text-green-600 font-mono mt-3"
              data-testid="clock-elapsed"
            >
              {elapsed()}
            </p>
            <p className="text-xs text-gray-400 mt-1">{t("ws.arbeitszeit_brutto", "Arbeitszeit (brutto)")}</p>
          </>
        ) : (
          <span className="inline-flex items-center gap-2 text-gray-400 font-medium text-base">
            <span className="w-3 h-3 rounded-full bg-gray-300" />
            {t("ws.nicht_eingestempelt")}
          </span>
        )}
      </div>

      {/* Project + Break (only when not clocked in) */}
      {!isClockedIn && (
        <div className="space-y-4 mb-6">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
              {t("ws.projekt")}
            </label>
            {activeProjects.length > 1 ? (
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger
                  className="h-14 text-base rounded-xl bg-white"
                  data-testid="clock-project-select"
                >
                  <SelectValue placeholder={t("ws.projekt_waehlen")} />
                </SelectTrigger>
                <SelectContent>
                  {activeProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-base py-3">
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : activeProjects.length === 1 ? (
              <div className="h-14 flex items-center px-4 bg-white border border-gray-200 rounded-xl text-gray-900">
                <MapPin className="w-5 h-5 mr-3 text-gray-400" />
                {activeProjects[0].name}
              </div>
            ) : (
              <div className="h-14 flex items-center px-4 bg-gray-100 border border-gray-200 rounded-xl text-gray-500">
                {t("ws.kein_projekt")}
              </div>
            )}
          </div>
          {user?.auto_pause_minutes > 0 && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl" data-testid="ws-auto-pause-info">
              <p className="text-xs text-amber-700 font-medium">
                {t("m.auto_pause_info")
                  .replace("{min}", user.auto_pause_minutes)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Main Button */}
      {isClockedIn ? (
        <button
          onClick={handleClockOut}
          disabled={loading}
          className="w-full h-16 bg-red-600 active:bg-red-700 text-white text-xl font-bold uppercase tracking-wider rounded-2xl disabled:opacity-50 transition-transform active:scale-[0.98]"
          data-testid="clock-out-btn"
        >
          {loading ? "..." : t("ws.ausstempeln")}
        </button>
      ) : (
        <button
          onClick={handleClockIn}
          disabled={loading || (!selectedProject && activeProjects.length !== 1)}
          className="w-full h-16 bg-green-600 active:bg-green-700 disabled:bg-gray-300 text-white text-xl font-bold uppercase tracking-wider rounded-2xl disabled:opacity-50 transition-transform active:scale-[0.98]"
          data-testid="clock-in-btn"
        >
          {loading ? "..." : t("ws.einstempeln")}
        </button>
      )}

      {/* User hint */}
      <p className="text-center text-xs text-gray-400 mt-4">{user?.name}</p>
    </div>
  );
}

// ──────────── TAB: Projekte ────────────
function ProjectsTab({ projects, navigate, t }) {
  const [selectedId, setSelectedId] = useState(null);
  const active = projects.filter((p) => p.status !== "abgeschlossen");
  const selected = active.find((p) => p.id === selectedId);

  const buildMapsUrl = (addr) => {
    const parts = [addr?.street, addr?.zip, addr?.city].filter(Boolean);
    if (parts.length === 0) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(", "))}`;
  };

  const callPhone = (phone) => {
    if (phone) window.open(`tel:${phone}`, "_self");
  };

  // ━━━ Detail View ━━━
  if (selected) {
    const addr = typeof selected.address === "object" ? selected.address : null;
    const addrText = addr ? [addr.name, addr.street, [addr.zip, addr.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") : null;
    const mapsUrl = buildMapsUrl(addr);
    const projectTitle = selected.display_name || selected.name;

    return (
      <div className="pb-4" data-testid="project-detail-view">
        {/* Back */}
        <div className="px-4 pt-4 pb-2">
          <button
            onClick={() => setSelectedId(null)}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 active:text-gray-800"
            data-testid="project-detail-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("ws.meine_projekte")}
          </button>
        </div>

        {/* Hero Image */}
        <div className="px-4 mb-4">
          {selected.project_image ? (
            <div className="w-full h-[200px] rounded-xl overflow-hidden shadow-sm" data-testid="project-detail-image">
              <img
                src={selected.project_image}
                alt={projectTitle}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
              />
              <div className="w-full h-full bg-gray-200 items-center justify-center hidden">
                <Building2 className="w-12 h-12 text-gray-400" />
              </div>
            </div>
          ) : (
            <div className="w-full h-[140px] rounded-xl bg-gray-200 flex items-center justify-center shadow-sm" data-testid="project-detail-image-placeholder">
              <Building2 className="w-14 h-14 text-gray-400" />
            </div>
          )}
        </div>

        {/* Title + Address */}
        <div className="px-4 mb-4">
          <h2 className="text-xl font-bold text-gray-900 leading-tight mb-1" data-testid="project-detail-name">
            {projectTitle}
          </h2>
          {selected.display_name && selected.display_name !== selected.name && (
            <p className="text-xs text-gray-400 mb-1">{selected.name}</p>
          )}
          {addrText && (
            <div className="flex items-center gap-1.5 mt-1">
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-500">{addrText}</span>
            </div>
          )}
        </div>

        {/* Navigation Button */}
        {mapsUrl && (
          <div className="px-4 mb-5">
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2.5 bg-gray-900 text-white rounded-xl py-4 text-[15px] font-semibold tracking-wide active:bg-gray-800 transition-colors shadow-sm"
              data-testid="project-detail-navigate-btn"
            >
              <Navigation className="w-5 h-5" />
              Navigation starten
            </a>
          </div>
        )}

        {/* Info Cards */}
        <div className="px-4 space-y-3">
          {/* Beschreibung */}
          {selected.description && (
            <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="project-detail-description">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Beschreibung</p>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{selected.description}</p>
            </div>
          )}

          {/* Ansprechpartner */}
          {(selected.contact_person || selected.contact_phone) && (
            <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="project-detail-contact">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-gray-400" />
                <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Ansprechpartner</p>
              </div>
              {selected.contact_person && (
                <p className="text-sm font-semibold text-gray-900 mb-1">{selected.contact_person}</p>
              )}
              {selected.contact_phone && (
                <button
                  onClick={() => callPhone(selected.contact_phone)}
                  className="flex items-center gap-2 text-sm font-medium text-blue-600 active:text-blue-800 py-1"
                  data-testid="project-detail-phone"
                >
                  <Phone className="w-4 h-4" />
                  {selected.contact_phone}
                </button>
              )}
            </div>
          )}

          {/* Projektleiter Kunde */}
          {selected.project_client_manager && (
            <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="project-detail-client-manager">
              <div className="flex items-center gap-2 mb-2">
                <HardHat className="w-4 h-4 text-gray-400" />
                <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Projektleiter Kunde</p>
              </div>
              <p className="text-sm font-semibold text-gray-900">{selected.project_client_manager}</p>
            </div>
          )}

          {/* Aufgaben Button */}
          <button
            onClick={() => navigate(`/app/workspace/${selected.id}`)}
            className="w-full bg-white border border-gray-200 text-gray-900 rounded-xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 active:bg-gray-50 transition-colors shadow-sm"
            data-testid="project-detail-tasks-btn"
          >
            <FolderOpen className="w-4 h-4" />
            Aufgaben anzeigen
          </button>
        </div>
      </div>
    );
  }

  // ━━━ List View ━━━
  if (active.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6" data-testid="projects-tab-empty">
        <Building2 className="w-14 h-14 text-gray-300 mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {t("ws.keine_projekte")}
        </h3>
        <p className="text-gray-500 text-sm text-center">
          {t("ws.keine_projekte_text")}
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 pb-4 space-y-3" data-testid="projects-tab">
      <div className="px-1 mb-1">
        <h2 className="text-lg font-bold text-gray-900">
          {t("ws.meine_projekte")}
        </h2>
        <p className="text-xs text-gray-400 mt-0.5">{active.length} aktiv</p>
      </div>
      {active.map((project) => {
        const addr = typeof project.address === "object" ? project.address : null;
        const addrShort = addr ? [addr.name, addr.street, addr.city].filter(Boolean).join(", ") : null;
        return (
          <button
            key={project.id}
            onClick={() => setSelectedId(project.id)}
            className="w-full bg-white rounded-xl overflow-hidden shadow-sm active:shadow-none active:bg-gray-50 transition-all text-left"
            data-testid={`project-card-${project.id}`}
          >
            {/* Card Image */}
            {project.project_image ? (
              <div className="w-full h-[150px] bg-gray-200">
                <img src={project.project_image} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-full h-[100px] bg-gray-200 flex items-center justify-center">
                <Building2 className="w-10 h-10 text-gray-400" />
              </div>
            )}
            {/* Card Body */}
            <div className="px-4 py-3">
              <h3 className="text-[17px] font-bold text-gray-900 leading-snug truncate">
                {project.display_name || project.name}
              </h3>
              {addrShort && (
                <p className="flex items-center gap-1 text-sm text-gray-500 mt-1 truncate">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  {addrShort}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ──────────── TAB: Heute (Tagesübersicht) ────────────
function TodayTab({ clockEntry, todayEntries, projects, t, dateLoc }) {
  const today = new Date();
  const totalMinutes = todayEntries.reduce(
    (sum, e) => sum + (e.work_time_minutes || 0),
    0
  );
  const totalHours = Math.floor(totalMinutes / 60);
  const totalMins = totalMinutes % 60;

  const isClockedIn = clockEntry && clockEntry.clock_in && !clockEntry.clock_out;

  return (
    <div className="px-4 pt-4 pb-4" data-testid="today-tab">
      <h2 className="text-lg font-bold text-gray-900 px-1 mb-4">
        {format(today, "EEEE, dd. MMMM", { locale: dateLoc })}
      </h2>

      {/* Summary Card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-500">{t("ws.gebuchte_stunden", "Gebuchte Stunden")}</span>
          <Clock className="w-5 h-5 text-gray-400" />
        </div>
        <p className="text-4xl font-bold text-gray-900 font-mono" data-testid="today-total-hours">
          {totalHours}:{totalMins.toString().padStart(2, "0")}
          <span className="text-lg text-gray-400 ml-1">h</span>
        </p>
        {isClockedIn && (
          <p className="text-sm text-green-600 font-medium mt-2 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            {t("ws.aktuell_eingestempelt", "Aktuell eingestempelt")}
          </p>
        )}
      </div>

      {/* Clock entry (if exists) */}
      {clockEntry && clockEntry.clock_in && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t("ws.stempeluhr")}
            </span>
            {isClockedIn ? (
              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                {t("ws.aktiv", "Aktiv")}
              </span>
            ) : (
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {t("ws.abgeschlossen", "Abgeschlossen")}
              </span>
            )}
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t("ws.projekt")}</span>
              <span className="font-medium text-gray-900">
                {clockEntry.project_name || "–"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("ws.beginn", "Beginn")}</span>
              <span className="font-mono font-medium text-gray-900">
                {clockEntry.clock_in
                  ? format(new Date(clockEntry.clock_in), "HH:mm")
                  : "–"}
              </span>
            </div>
            {clockEntry.clock_out && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t("ws.ende", "Ende")}</span>
                  <span className="font-mono font-medium text-gray-900">
                    {format(new Date(clockEntry.clock_out), "HH:mm")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t("ws.pause")}</span>
                  <span className="font-mono text-gray-700">
                    {Math.floor((clockEntry.total_break_seconds || 0) / 60)} min
                  </span>
                </div>
                <div className="flex justify-between pt-1.5 border-t border-gray-100">
                  <span className="font-medium text-gray-900">{t("ws.netto", "Netto")}</span>
                  <span className="font-mono font-bold text-green-700">
                    {clockEntry.net_work_time || "–"}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Time Entries */}
      {todayEntries.length > 0 && (
        <>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mb-2 mt-4">
            {t("ws.zeitbuchungen", "Zeitbuchungen")}
          </h3>
          <div className="space-y-2">
            {todayEntries.map((entry) => (
              <div
                key={entry.id}
                className="bg-white border border-gray-200 rounded-xl p-4"
                data-testid={`today-entry-${entry.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-900 text-sm">
                    {entry.project_name || t("ws.projekt")}
                  </span>
                  <span className="font-mono font-bold text-gray-900 text-sm">
                    {entry.work_time_minutes
                      ? `${Math.floor(entry.work_time_minutes / 60)}:${(
                          entry.work_time_minutes % 60
                        )
                          .toString()
                          .padStart(2, "0")} h`
                      : "–"}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {entry.work_start} – {entry.work_end}
                  {entry.break_minutes > 0 &&
                    ` (${entry.break_minutes} ${t("ws.min_pause")})`}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Empty state */}
      {!clockEntry && todayEntries.length === 0 && (
        <div className="text-center py-12">
          <CalendarCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t("ws.keine_eintraege")}</p>
        </div>
      )}
    </div>
  );
}

// ──────────── TAB: Profil ────────────
function ProfileTab({ user, onLogout, t }) {
  return (
    <div className="px-5 pt-8 pb-4" data-testid="profile-tab">
      <div className="flex flex-col items-center mb-8">
        <div className="w-20 h-20 rounded-full bg-gray-900 flex items-center justify-center text-white text-2xl font-bold mb-3">
          {user?.name?.charAt(0)?.toUpperCase() || "?"}
        </div>
        <h2 className="text-xl font-bold text-gray-900">{user?.name}</h2>
        <p className="text-sm text-gray-500">{user?.email}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl divide-y divide-gray-100">
        <div className="px-5 py-4 flex justify-between">
          <span className="text-sm text-gray-500">{t("ws.rolle", "Rolle")}</span>
          <span className="text-sm font-medium text-gray-900 capitalize">{user?.role}</span>
        </div>
      </div>

      <button
        onClick={onLogout}
        className="w-full mt-8 h-14 border-2 border-red-200 text-red-600 rounded-2xl font-semibold text-base flex items-center justify-center gap-2 active:bg-red-50 transition-colors"
        data-testid="profile-logout-btn"
      >
        <LogOut className="w-5 h-5" />
        {t("ws.abmelden")}
      </button>
    </div>
  );
}

// ──────────── MAIN COMPONENT ────────────
export default function WorkspaceMobile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang } = useTranslation();
  const [activeTab, setActiveTab] = useState("clock");
  const [projects, setProjects] = useState([]);
  const [clockEntry, setClockEntry] = useState(null);
  const [todayEntries, setTodayEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const dateLoc = lang === "sk" ? sk : lang === "en" ? enUS : de;

  const loadData = useCallback(async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const [projRes, clockRes, entriesRes] = await Promise.all([
        authFetch(`${API}/projects`),
        authFetch(`${API}/clock/status`),
        authFetch(`${API}/time-entries?date_from=${today}&date_to=${today}`),
      ]);

      if (projRes.ok) {
        const data = await projRes.json();
        setProjects(data);
      }
      if (clockRes.ok) {
        const data = await clockRes.json();
        setClockEntry(data);
      }
      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setTodayEntries(data);
      }
    } catch (err) {
      console.error("Workspace load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh when switching tabs
  useEffect(() => {
    if (!loading) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Tab focus listener - refresh data when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadData();
      }
    };
    const handleFocus = () => {
      loadData();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [loadData]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="workspace-mobile">
      <ImpersonationBanner />

      {/* Tab Content */}
      {activeTab === "clock" && (
        <ClockTab
          user={user}
          projects={projects}
          clockEntry={clockEntry}
          onRefresh={loadData}
          t={t}
          dateLoc={dateLoc}
        />
      )}
      {activeTab === "projects" && (
        <ProjectsTab projects={projects} navigate={navigate} t={t} />
      )}
      {activeTab === "today" && (
        <TodayTab
          clockEntry={clockEntry}
          todayEntries={todayEntries}
          projects={projects}
          t={t}
          dateLoc={dateLoc}
        />
      )}
      {activeTab === "profile" && (
        <ProfileTab user={user} onLogout={handleLogout} t={t} />
      )}

      <BottomNav active={activeTab} onChange={setActiveTab} t={t} />
    </div>
  );
}
